import {DrwRenderingMeta} from "./Openings";
import { SubAPI } from "./SubAPI";
import {Paginated} from "./Users";
import {DateString} from "./Projects";
import {TID} from "../misc/lists/ListCommon";
import {PrepDetail, PrepFromGroup} from "./QuoteHardware";
import {
    AnchorDetail,
    ByOthersSetting,
    DoorCore,
    DoorElevation, DoorSeam,
    DrawingWallLabel,
    FrameProfile,
    ScreenElevation,
    WoodDetailItem
} from "./Openings";

export class QuoteOpenings extends SubAPI {
    list(input: {
        ids?: string; // csv
        project: number
        alternative: number
        category?: "frame" | "door" | "hardware";
        offset?: number;
        type?: "add-hw-find-opening-number" | "have-no-hardware-group" | "for-hardware-group";
        search?: string;
        hwGroup?: number;
    }) {
        return this.fetcher.get<Paginated<QuoteOpening>>("/api/quote-openings", input)
    }

    listDrawingWallLabels(input: {
        project: number;
        alternative: number;
        offset?: number;
    }) {
        return this.fetcher.get<Paginated<DrawingWallLabel>>("/api/quote-opening/drawing-wall-labels", input)
    }

    updateDrawingWallLabel(props: DrawingWallLabel) {
        return this.fetcher.post<{}>("/api/quote-opening/drawing-wall-label", props)
    }

    listScreenElevations(input: {
        project: number;
        alternative: number;
        offset?: number;
    }) {
        return this.fetcher.get<Paginated<ScreenElevation>>("/api/quote-opening/screen-elevations", input)
    }

    updateScreenElevation(input: ScreenElevation) {
        return this.fetcher.post<{}>("/api/quote-opening/screen-elevation", input)
    }

    reSequence(input: {
        project: number;
        alternative: number;
        idOrder: number[]
    }) {
        return this.fetcher.post<{}>("/api/quote-opening/re-sequence", input)
    }

    reOrder(input: {
        project: number;
        alternative: number|null;
        idOrder: number[]
    }) {
        return this.fetcher.post<{}>("/api/quote-opening/re-order", input)
    }

    upsert(input: QuoteOpening | {project: number, name: string, quoteAlternative: number}) {
        return this.fetcher.post<TID & {ids: number[]}>("/api/quote-opening", input)
    }

    upsertBulk(input: (QuoteOpening | {project: number, name: string, quoteAlternative: number})[]) {
        return this.fetcher.post<TID & {ids: number[]}>("/api/quote-opening/bulk", input)
    }

    listDoorElevations(input: {
        project: number;
        alternative: number;
        offset?: number;
    }) {
        return this.fetcher.get<Paginated<DoorElevation>>("/api/quote-opening/door-elevations", input)
    }

    updateDoorElevation(input: DoorElevation) {
        return this.fetcher.post<{}>("/api/quote-opening/door-elevation", input)
    }

    bulkAction(input: {
        ids: number[];
        action: "archive" | "duplicate" | "set-by-others";
        door?: ByOthersSetting;
        frame?: ByOthersSetting;
    }) {
        return this.fetcher.post<{}>("/api/quote-opening/bulk-action", input)
    }

    listFrameProfiles(props: {
        project: number;
        alternative: number;
    }) {
        return this.fetcher.get<FrameProfile[]>("/api/quote-opening/frame-profiles", props)
    }

    listAnchors(props: {
        project: number;
        alternative: number;
    }) {
        return this.fetcher.get<AnchorDetail[]>("/api/quote-opening/anchors", props)
    }

    listWoodDetails(props: {
        project: number;
        alternative: number;
    }) {
        return this.fetcher.get<WoodDetailItem[]>("/api/quote-opening/wood-detail", props);
    }

    updateWoodDetail(input: WoodDetailItem) {
        return this.fetcher.post("/api/quote-opening/wood-detail", input);
    }

    listDoorCores(props: {
        project: number;
        alternative: number;
    }) {
        return this.fetcher.get<DoorCore[]>("/api/quote-opening/door-cores", props);
    }

    upsertDoorCore(input: DoorCore) {
        return this.fetcher.post("/api/quote-opening/door-core", input);
    }

    listDoorSeams(props: {
        project: number;
        alternative: number;
    }) {
        return this.fetcher.get<DoorSeam[]>("/api/quote-opening/door-seams", props);
    }

    upsertDoorSeam(input: DoorSeam) {
        return this.fetcher.post("/api/quote-opening/door-seam", input);
    }
}

export type QuoteOpening = {
    id: number;
    name: string;
    project: number;
    quoteAlternative: number;
    seqNumber: number;
    qty: number;

    hardwareGroup?: number;
    hardwareGroupName?: string;
    hardwareGroupPrepConfig?: PrepDetail[];
    hardwareGroupPrepConfigIsCustom: boolean;
    prepFromGroup?: PrepFromGroup;
    prepIsCustom?: boolean;
    hwDescriptiveName?: string;
    hardwareGroupShortcode?: number;
    hardwareGroupShortcodeName?: string;

    locationOne: string;
    locationTransition: "to"|"from"|"";
    locationTwo: string;
    floor: string;
    phase: string;
    exterior: boolean;
    openingType: string;
    nominalWidth: string;
    inactiveDoorWidth: string;
    nominalHeight: string;
    handing: string;
    flipDoubleActiveHanding: boolean;
    label: string;
    frameSeries: string;
    frameMaterial: string;
    frameGauge: string;

    screenElevation: string;
    screenElevationFileName?: string;
    screenElevationFile?: number;
    screenElevationOutsideWidth: string;
    screenElevationOutsideHeight: string;
    hasFixedMullion: boolean;

    frameProfile: string;
    frameProfileFile: number | null;
    frameProfileFileName: string | null;

    // the new drw-based elevation links. When drwScreenElevation is set the quote opening uses
    // the drw elevation for that spec instead of the legacy file-based screenElevation above.
    // drwScreenElevationName is the resolved screen name (door/hardware analogous).
    drwScreenElevation?: number;
    drwScreenElevationName?: string;
    // the linked screen rendering's measurements (drw openings only; legacy openings use the
    // screenElevationOutside* fields).
    drwScreenRenderingMeta?: DrwRenderingMeta | null;
    // multi-opening screens: on a secondary opening, the opening that owns the frame and the
    // door slot (by name) this opening fills on the primary's screen. Null on a primary.
    drwScreenPrimaryOpening?: number | null;
    drwScreenPrimaryOpeningName?: string | null;
    drwScreenSlot?: string | null;
    drwDoorElevation?: number;
    drwDoorElevationName?: string;
    // true when a drawn lite of the drw door elevation has thermal glass. Read-only.
    drwDoorThermal?: boolean;
    drwHardwareElevation?: number;
    drwHardwareElevationName?: string;

    // submittal rendering file ids + render-group labels, attached per quote opening by the
    // elevation tool's render-on-save (same as production Opening). Printouts read these and build
    // api.files.downloadLink(id); the label prints on the image as "{elevName}-{label}".
    drwScreenRendering?: number;
    drwDoorRendering?: number;
    drwHardwareRendering?: number;
    drwScreenElevationLabel?: string;
    drwDoorElevationLabel?: string;
    drwHardwareElevationLabel?: string;

    jambDepth: string;

    drawingWallLabel: string;
    drawingWallLabelFile?: number;
    drawingWallLabelFileName?: string;

    frameConstruction: string;
    frameHardwarePreps: string;
    doorSeries: string;
    doorMaterial: string;
    doorGauge: string;
    doorThickness: string;

    doorElevation: string;
    doorElevationFile?: number;
    doorElevationFileName?: string;
    inactiveDoorElevation: string;
    inactiveDoorElevationFile?: number;
    inactiveDoorElevationFileName?: string;

    doorLiteKitActiveFile?: number;
    doorLiteKitInactiveFile?: number;

    doorCore: string;
    doorSeam: string;
    doorFinish: string;
    notes: string;

    // null = inherit by-others from the series' supplier category; non-null = explicit override
    doorActiveByOthers: boolean | null;
    doorInactiveByOthers: boolean | null;
    frameByOthers: boolean | null;

    // effective by-others computed by the backend: the explicit override above when set,
    // otherwise inherited from the series' supplier category. Read-only.
    doorActiveByOthersCalc: boolean;
    doorInactiveByOthersCalc: boolean;
    frameByOthersCalc: boolean;

    frameAnchorProduct?: number;
    frameAnchorProductName?: string;
    frameAnchorDetailFile?: number;
    frameAnchorQtyOverride: number | null;
    // the linked screen elevation's anchors: whether it has any, and their totals from its ready
    // render ("P&S x4, Z x2"; null until the render is ready). When it has any, they replace the
    // anchor columns above.
    drwHasAnchors: boolean;
    drwAnchorSummary: string | null;
    // the products of those anchors; empty until the render is ready.
    drwAnchorProducts: number[] | null;

    sortOrder: number;

    createdAt: DateString;
    updatedAt: DateString;
    archived: boolean;

    degreesOfOpeningActive: number | null;
    degreesOfOpeningInactive: number | null;
}